// Shop LAYOUT and zone/alcove dressing.
//
// The hard layout numbers the rest of the shop is built against (room half
// width, till position, the four bin-run X lines and the five aisle X lines),
// the bulb-preset table the light recipes draw from, the five floor ZONES that
// tile the room front-to-back along Z, and the five ALCOVES the player can walk
// into.
//
// Each zone and alcove carries four recipes read by the fixture and texture
// layers: wall, floor, fixture and light. `light.main` is a bulb preset from
// LAMPS; `light.accent` spreads a preset (or gives its own colour) and adds the
// accent's kind and intensity.
//
// Zone `z` is a [near, far] pair along Z. Alcove `side` is 1 or -1 for which
// wall the alcove is cut into.
//
// Recovered from the production bundle. Every numeric constant and every string
// is copied from it exactly. The only two places this file departs from the
// bundle's bytes are `entrance: true` where the bundle has `!0`, and the
// optional chain in genreColorInt where the bundle has the downlevelled
// `== null ? void 0 :` form. Both evaluate identically.

import { GENRE_BY_ID as genresById } from './catalogue.js';

const ROOM_HALF_W = 3.9;

const COUNTER_POS = {
    x: 0,
    z: -1.35
};

const BIN_RUNS_X = [-2.45, -.82, .82, 2.45];

const AISLE_X = [-3.4, -1.65, 0, 1.65, 3.4];

const LAMPS = {
    TUNGSTEN_WARM: {
        color: 16751173,
        kelvin: 2400
    },
    TUNGSTEN: {
        color: 16757094,
        kelvin: 2700
    },
    FLUORESCENT: {
        color: 16773592,
        kelvin: 3500
    },
    COOL_STRIP: {
        color: 14216447,
        kelvin: 4200
    },
    NEON_RED: {
        color: 16722492
    },
    NEON_CYAN: {
        color: 2807039
    },
    BRASS_PICTURE: {
        color: 16763274,
        kelvin: 2500
    },
    BARE_BULB: {
        color: 16763955,
        kelvin: 2200
    }
};

const ZONES = [{
    id: "front",
    name: "FRONT OF HOUSE",
    genres: ["rockpsych", "hiphop"],
    z: [4.6, 8.2],
    blurb: "Loudest, nearest the till. Flyers stapled over flyers.",
    wall: {
        kind: "flyer-collage",
        density: 1,
        base: 3813672
    },
    floor: {
        kind: "plank",
        tint: 3617839,
        wear: 1
    },
    fixture: {
        kind: "crate",
        timber: "blackened",
        tint: 4866618
    },
    light: {
        main: LAMPS.TUNGSTEN,
        accent: {
            ...LAMPS.NEON_RED,
            kind: "sign",
            intensity: 2.2
        },
        bias: 0
    }
}, {
    id: "groove",
    name: "GROOVE FLOOR",
    genres: ["soul", "rnb", "disco"],
    z: [1.2, 4.6],
    blurb: "The only shiny thing in the shop. Chrome trim, mirror specks.",
    wall: {
        kind: "foil-strip",
        density: .6,
        base: 3810368,
        sheen: .35
    },
    floor: {
        kind: "plank",
        tint: 3617325,
        wear: .9
    },
    fixture: {
        kind: "crate",
        timber: "raw",
        tint: 10521190,
        trim: "chrome"
    },
    light: {
        main: LAMPS.TUNGSTEN,
        accent: {
            color: 16767472,
            kind: "mirrorball",
            intensity: 1.4,
            speckles: 9
        },
        bias: .05
    }
}, {
    id: "machine",
    name: "THE MACHINE ROOM",
    genres: ["electronic", "pop"],
    z: [-2.2, 1.2],
    blurb: "Sealed concrete, black crates, the only cool light in the building.",
    wall: {
        kind: "painted-flat",
        density: .25,
        base: 1974566
    },
    floor: {
        kind: "concrete",
        tint: 2894895,
        wear: .5
    },
    fixture: {
        kind: "crate",
        timber: "painted-black",
        tint: 2763310
    },
    light: {
        main: LAMPS.COOL_STRIP,
        accent: {
            ...LAMPS.NEON_CYAN,
            kind: "sign",
            intensity: 2
        },
        bias: -.05
    }
}, {
    id: "global",
    name: "LATIN · WORLD · BLUES",
    genres: ["latin", "world", "blues"],
    z: [-5.4, -2.2],
    blurb: "Sun-bleached sleeves and a rug someone brought back from somewhere.",
    wall: {
        kind: "sunfaded-poster",
        density: .8,
        base: 3029540,
        fade: .4
    },
    floor: {
        kind: "plank-rug",
        tint: 3420460,
        wear: 1
    },
    fixture: {
        kind: "crate",
        timber: "raw",
        tint: 10259054
    },
    light: {
        main: LAMPS.TUNGSTEN_WARM,
        accent: {
            ...LAMPS.BARE_BULB,
            kind: "bulb-bare",
            intensity: 1.8
        },
        bias: 0
    }
}, {
    id: "quiet",
    name: "THE QUIET END",
    genres: ["jazz", "classical", "folk", "gospel", "soundtracks"],
    z: [-8.2, -5.4],
    blurb: "One lamp, framed prints, a chair nobody sits in.",
    wall: {
        kind: "framed-prints",
        density: .35,
        base: 2304563
    },
    floor: {
        kind: "plank",
        tint: 3223083,
        wear: .6
    },
    fixture: {
        kind: "shelf-deep",
        timber: "dark-stained",
        tint: 4865070
    },
    light: {
        main: LAMPS.TUNGSTEN_WARM,
        accent: {
            ...LAMPS.BRASS_PICTURE,
            kind: "picture",
            intensity: 2.2
        },
        bias: -.12
    }
}];

const ALCOVES = [{
    id: "entry-hall",
    name: "NSPIRE RECORDS",
    entrance: true,
    genres: [],
    alcove: {
        side: 1,
        z: -3.4,
        w: 4.2,
        d: 3.4,
        openingW: 2.6,
        ceil: 3.2
    },
    blurb: "Glass door, worn mat, gig posters either side, name over the arch.",
    wall: {
        kind: "flyer-collage",
        density: 1,
        base: 2762273
    },
    floor: {
        kind: "plank",
        tint: 3025702,
        wear: 1
    },
    fixture: {
        kind: "crate",
        timber: "raw",
        tint: 10259054
    },
    light: {
        main: LAMPS.FLUORESCENT,
        accent: {
            ...LAMPS.NEON_RED,
            kind: "sign",
            intensity: 1.6
        },
        bias: 0
    }
}, {
    id: "reggae-room",
    name: "REGGAE / DUB",
    genres: ["reggae"],
    alcove: {
        side: -1,
        z: -4.6,
        w: 3,
        d: 3,
        openingW: 2.2,
        ceil: 3.2
    },
    blurb: 'Speaker stack, bare yellow bulb, 7" pressings in orange crates.',
    wall: {
        kind: "sunfaded-poster",
        density: .9,
        base: 3029540
    },
    floor: {
        kind: "plank",
        tint: 3420460,
        wear: 1
    },
    fixture: {
        kind: "crate",
        timber: "raw",
        tint: 10259054
    },
    light: {
        main: LAMPS.TUNGSTEN_WARM,
        accent: {
            ...LAMPS.BARE_BULB,
            kind: "bulb-bare",
            intensity: 2
        },
        bias: 0
    }
}, {
    id: "listening-room",
    name: "SOUL / LISTENING ROOM",
    genres: ["soul"],
    alcove: {
        side: -1,
        z: .4,
        w: 3.2,
        d: 3.1,
        openingW: 2.3,
        ceil: 3.2
    },
    blurb: "A back room done out like a front parlour. Carpet, not boards.",
    wall: {
        kind: "flock-wallpaper",
        density: .5,
        base: 5906472,
        pattern: "damask"
    },
    floor: {
        kind: "carpet",
        tint: 2757144,
        wear: .7
    },
    fixture: {
        kind: "browser-low",
        timber: "dark-stained",
        tint: 5455916,
        trim: "brass"
    },
    light: {
        main: LAMPS.TUNGSTEN_WARM,
        accent: {
            ...LAMPS.BRASS_PICTURE,
            kind: "picture",
            intensity: 2.6
        },
        bias: .02
    }
}, {
    id: "jazz-room",
    name: "JAZZ CORNER",
    genres: ["jazz"],
    alcove: {
        side: 1,
        z: 1.2,
        w: 3,
        d: 3,
        openingW: 2.2,
        ceil: 3.2
    },
    blurb: "Dark stained cabinets, one brass picture light, a chair nobody sits in.",
    wall: {
        kind: "framed-prints",
        density: .35,
        base: 2304563
    },
    floor: {
        kind: "plank",
        tint: 3223083,
        wear: .6
    },
    fixture: {
        kind: "shelf-deep",
        timber: "dark-stained",
        tint: 4865070
    },
    light: {
        main: LAMPS.TUNGSTEN_WARM,
        accent: {
            ...LAMPS.BRASS_PICTURE,
            kind: "picture",
            intensity: 2.2
        },
        bias: -.1
    }
}, {
    id: "machine-room",
    name: "ELECTRONIC ANNEXE",
    genres: ["electronic"],
    alcove: {
        side: 1,
        z: 5.4,
        w: 3,
        d: 2.9,
        openingW: 2.2,
        ceil: 3.2
    },
    blurb: "Sealed concrete, black crates, the only cool light in the building.",
    wall: {
        kind: "painted-flat",
        density: .25,
        base: 1974566
    },
    floor: {
        kind: "concrete",
        tint: 2894895,
        wear: .5
    },
    fixture: {
        kind: "crate",
        timber: "painted-black",
        tint: 2763310
    },
    light: {
        main: LAMPS.COOL_STRIP,
        accent: {
            ...LAMPS.NEON_CYAN,
            kind: "sign",
            intensity: 1.4
        },
        bias: -.05
    }
}];

// Bare expression statement, preserved verbatim from the bundle; it has no
// effect as written.
[...ZONES, ...ALCOVES];

function genreColorInt(genreId) {
    const hex = genresById[genreId]?.color || "#c8b89a";
    return parseInt(hex.slice(1), 16);
}

// Bare expression statement, preserved verbatim from the bundle; it has no
// effect as written. It evaluates to the ZONES near edges above -8, sorted
// ascending: [-5.4, -2.2, 1.2, 4.6].
ZONES.map(zone => zone.z[0]).filter(nearZ => nearZ > -8).sort((a, b) => a - b);

export { ROOM_HALF_W, COUNTER_POS, BIN_RUNS_X, AISLE_X, ZONES, ALCOVES, genreColorInt };
